import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { auth, db } from "../Firebase";

import {
  doc,
  setDoc,
  collection,
  query,
  where,
  limit,
  onSnapshot,
  getDocs,
  serverTimestamp
} from "firebase/firestore";

import { onAuthStateChanged } from "firebase/auth";

import { toast } from "react-toastify";
import "../styles/Courses.css";

import { useCartWishlist } from "../Context/CartWishlistContext";

const PAGE_SIZE = 8;

const Courses = () => {
  const navigate = useNavigate();

  const [courses, setCourses] = useState([]);
  const [lastDoc, setLastDoc] = useState(null);
  const [loading, setLoading] = useState(true);

  const [purchasedIds, setPurchasedIds] = useState([]);

  const {
    addToWishlist,
    removeFromWishlist,
    wishlistCourses,
    addToCart
  } = useCartWishlist();

  const wishlistIds = wishlistCourses.map(
    (w) => w.id
  );

  const [cartIds, setCartIds] = useState([]);

  /* ================= SYNC CART ================= */
  useEffect(() => {
    const syncCart = () => {
      const cart =
        JSON.parse(localStorage.getItem("cart")) || [];
      setCartIds(cart.map((c) => c.id));
    };

    syncCart();
    window.addEventListener("storage", syncCart);
    return () =>
      window.removeEventListener("storage", syncCart);
  }, []);

  /* ================= FETCH PURCHASED ================= */
  useEffect(() => {
    const unsub = onAuthStateChanged(auth, (user) => {
      if (!user) {
        setPurchasedIds([]);
        return;
      }

      const q = query(
        collection(db, "orders"),
        where("userId", "==", user.uid)
      );

      return onSnapshot(q, (snap) => {
        setPurchasedIds(
          snap.docs.map((d) => d.data().courseId)
        );
      });
    });

    return () => unsub();
  }, []);

  /* ================= FETCH COURSES ================= */
  useEffect(() => {
    const q = query(
      collection(db, "courses"),
      where("isPublished", "==", true),
      limit(PAGE_SIZE)
    );

    const unsubscribe = onSnapshot(q, (snapshot) => {
      const sorted = snapshot.docs
        .filter((d) => d.data().createdAt)
        .sort(
          (a, b) =>
            b.data().createdAt.seconds -
            a.data().createdAt.seconds
        )
        .map((d) => ({ id: d.id, ...d.data() }));

      setCourses(sorted);
      setLastDoc(sorted[sorted.length - 1] || null);
      setLoading(false);
    });

    return () => unsubscribe();
  }, []);

  /* ================= LOAD MORE ================= */
  const loadMore = async () => {
    if (!lastDoc) return;

    setLoading(true);

    const q = query(
      collection(db, "courses"),
      where("isPublished", "==", true),
      limit(PAGE_SIZE * 2)
    );

    const snapshot = await getDocs(q);

    const sorted = snapshot.docs
      .filter((d) => d.data().createdAt)
      .sort(
        (a, b) =>
          b.data().createdAt.seconds -
          a.data().createdAt.seconds
      );

    const index = sorted.findIndex(
      (d) => d.id === lastDoc.id
    );

    const next = sorted.slice(
      index + 1,
      index + 1 + PAGE_SIZE
    );

    setCourses((prev) => [
      ...prev,
      ...next.map((d) => ({
        id: d.id,
        ...d.data()
      }))
    ]);

    setLastDoc(next[next.length - 1] || null);
    setLoading(false);
  };

  /* ================= BUY NOW ================= */
  const buyNow = async (course) => {
  const user = auth.currentUser;

  if (!user) {
    toast.error("Please login first");
    navigate("/login");
    return;
  }

  /* ---------- FREE COURSE ---------- */
  if (course.isFree || course.price === 0) {

    // Save order
    await setDoc(
      doc(db, "orders", `${user.uid}_${course.id}`),
      {
        userId: user.uid,
        courseId: course.id,
        pricePaid: 0,
        purchasedAt: new Date()
      }
    );

    // Create course timing entry
    await setDoc(
      doc(db, "courseTiming", `${user.uid}_${course.id}`),
      {
        userId: user.uid,
        courseId: course.id,

        duration: course.duration || 0,
        watchTime: 0,

        progressPercentage: 0,

        lastVideoIndex: 0,
        lastVideoSecond: 0,

        completed: false,
        completedVideoIds: [],

        createdAt: serverTimestamp()
      }
    );

    toast.success("Enrolled successfully");
    navigate("/myCourse");
    return;
  }

  /* ---------- PAID COURSE ---------- */
  navigate("/checkout", { state: { course } });
};


  return (
    <div className="courses-container">
      <h2 className="page-title">Popular Courses</h2>

      {loading && <p align="center">Loading…</p>}

      <div className="courses-grid">
        {courses.map((course) => {
          const isPurchased =
            purchasedIds.includes(course.id);
          const inCart = cartIds.includes(course.id);
          const inWishlist =
            wishlistIds.includes(course.id);

          const isFree =
            course.isFree || course.price === 0;

          const hasDiscount =
            course.discountPercent > 0;

          const discountedPrice = hasDiscount
            ? Math.round(
                course.price *
                  (1 -
                    course.discountPercent / 100)
              )
            : course.price;

          return (
            <div key={course.id} className="course-card">

              {/* WISHLIST HEART */}
              <span
                className={`wishlist ${
                  inWishlist ? "active" : ""
                }`}
                onClick={() =>
                  inWishlist
                    ? removeFromWishlist(course.id)
                    : addToWishlist(course)
                }
              >
                ♥
              </span>

              <div
                className="image-wrapper"
                onClick={() =>
                  isPurchased
                    ? navigate(
                        `/myCourseDetails/${course.id}`
                      )
                    : navigate(
                        `/courseDetails/${course.id}`
                      )
                }
              >
                <img
                  src={course.thumbnail}
                  alt={course.title}
                />
              </div>

              <div className="course-content">
                <div onClick={() =>
                    isPurchased
                      ? navigate(
                          `/myCourseDetails/${course.id}`
                        )
                      : navigate(
                          `/courseDetails/${course.id}`
                        )
                  } >
                  <h3>{course.title}</h3>
                </div>
                <div className="price-row">
                  {(hasDiscount || isFree) && (
                    <span className="original-price">
                      ₹{course.price}
                    </span>
                  )}

                  {isFree ? (
                    <span className="free">Free</span>
                  ) : (
                    <span className="current-price">
                      ₹{discountedPrice}
                    </span>
                  )}
                </div>

                <div className="course-actions">
                  <button
                    className="btn-outline"
                    disabled={isPurchased || inCart}
                    onClick={() => addToCart(course)}
                  >
                    {isPurchased
                      ? "Enrolled"
                      : inCart
                      ? "In Cart"
                      : "Add to Cart"}
                  </button>

                  <button
                    className="btn-primary"
                    onClick={() => {
                      if (isPurchased) {
                        navigate(`/myCourseDetails/${course.id}`);
                      } else {
                        buyNow(course);
                      }
                    }}
                  >
                    {isPurchased
                      ? "Go to Course"
                      : "Buy Now"}
                  </button>
                </div>
              </div>
            </div>
          );
        })}
      </div>

      {lastDoc && (
        <div style={{ textAlign: "center", marginTop: 40 }}>
          <button
            className="search-btn"
            onClick={loadMore}
          >
            Load More
          </button>
        </div>
      )}
    </div>
  );
};

export default Courses;
